import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Lock, RefreshCw, Info } from 'lucide-react';
import { KeyRollModal } from '@/components/KeyCard';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import type {
  ApiKeyStatus,
  ApiKeyGeneration,
} from '@kirin/api-client/contracts/account';
import type { ApiKeyGenerationVariables } from '@/hooks/api/useApiKeys';
import { formatDateTime } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

// Props interface for HeaderKeyControl component
export interface HeaderKeyControlProps {
  statusQuery: UseQueryResult<ApiKeyStatus | undefined, unknown>;
  generateMutation: UseMutationResult<
    ApiKeyGeneration,
    unknown,
    ApiKeyGenerationVariables,
    unknown
  >;
  /** Support users may open the roll modal but can't actually generate a key. */
  disableGenerate?: boolean;
}

export function HeaderKeyControl({
  statusQuery,
  generateMutation,
  disableGenerate = false,
}: HeaderKeyControlProps) {
  const timeZone = useDisplayTimeZone();
  const [open, setOpen] = useState(false);
  const { data: status, isLoading: statusLoading } = statusQuery;
  const hasKey = status?.hasKey ?? false;

  const onGenerate = async (): Promise<string> => {
    const result = await generateMutation.mutateAsync({ hadKeyBefore: hasKey });
    return result.apiKey;
  };

  const loading = generateMutation.isPending || statusLoading;

  return (
    <>
      <TooltipProvider>
        <div className="inline-flex max-w-full min-w-0 items-center gap-2 sm:gap-3 rounded-full border px-[21px] py-1.5 bg-surface-inset overflow-x-auto">
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
              API Access
            </span>
            <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </div>

          {hasKey ? (
            <>
              <Badge variant="secondary" className="h-5 px-2 flex-shrink-0">
                Active
              </Badge>
              {status?.keyPrefix && (
                <span className="text-xs text-muted-foreground max-w-[10rem] truncate flex-shrink-0">
                  Prefix: <code>{status.keyPrefix}</code>
                </span>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="inline-flex items-center rounded hover:bg-slate-100 dark:hover:bg-slate-800 px-1 flex-shrink-0"
                    aria-label="Key details"
                  >
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <div className="text-xs space-y-1">
                    <div>
                      <span className="text-muted-foreground">Created:</span>{' '}
                      {formatDateTime(status?.createdAt, timeZone)}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last used:</span>{' '}
                      {formatDateTime(status?.lastUsedAt, timeZone)}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </>
          ) : (
            <span className="text-xs text-muted-foreground flex-shrink-0">
              No API key
            </span>
          )}

          <Button
            size="sm"
            variant={hasKey ? 'outline' : 'default'}
            className="h-7 flex-shrink-0 ml-1"
            onClick={() => setOpen(true)}
            disabled={loading}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`}
            />
            {hasKey ? 'Rotate' : 'Create'}
          </Button>
        </div>
      </TooltipProvider>

      {open && (
        <KeyRollModal
          hasKey={hasKey}
          onGenerate={onGenerate}
          onClose={() => setOpen(false)}
          disableGenerate={disableGenerate}
        />
      )}
    </>
  );
}
